import { useRef, useCallback } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

interface MicroInteractionOptions {
  /** Scale factor on press (default 0.98) */
  pressScale?: number
  /** Duration of the press tween in seconds */
  pressDuration?: number
}

/**
 * Click/press micro-interaction: scale-down on mousedown, spring back on mouseup.
 * Uses `useGSAP()` for setup — event handlers call `gsap.to()` directly.
 *
 * Usage:
 *   const { ref, onMouseDown } = useMicroInteraction()
 *   <button ref={ref} onMouseDown={onMouseDown}>…</button>
 */
export function useMicroInteraction<T extends HTMLElement = HTMLButtonElement>({
  pressScale = 0.98,
  pressDuration = 0.1,
}: MicroInteractionOptions = {}) {
  const ref = useRef<T>(null)

  // Confirm the element is ready (no animation on mount needed)
  useGSAP(() => {}, { scope: ref })

  const onMouseDown = useCallback(() => {
    if (!ref.current) return
    gsap.to(ref.current, { scale: pressScale, duration: pressDuration, ease: 'power2.out' })
  }, [pressScale, pressDuration])

  const onMouseUp = useCallback(() => {
    if (!ref.current) return
    gsap.to(ref.current, { scale: 1, duration: 0.2, ease: 'elastic.out(1, 0.5)' })
  }, [])

  const onMouseLeave = useCallback(() => {
    if (!ref.current) return
    // Snap back if the pointer leaves while pressed
    gsap.to(ref.current, { scale: 1, duration: 0.15, ease: 'power2.out' })
  }, [])

  return { ref, onMouseDown, onMouseUp, onMouseLeave }
}
